import { useState } from "react";
import { toast } from "sonner";
import { Translate, ArrowCounterClockwise, CaretDown } from "@phosphor-icons/react";
import api from "@/lib/api";

const LANGUAGE_NAMES = {
  en: "English",
  de: "German", fr: "French", it: "Italian", es: "Spanish", pt: "Portuguese",
  nl: "Dutch", pl: "Polish", sv: "Swedish", da: "Danish", no: "Norwegian",
  fi: "Finnish", ru: "Russian", cs: "Czech", hu: "Hungarian", el: "Greek",
  tr: "Turkish", ar: "Arabic", he: "Hebrew", ja: "Japanese", ko: "Korean",
  zh: "Chinese", hi: "Hindi", th: "Thai", vi: "Vietnamese",
};

// Order used in the "Looks wrong?" picker — most-common first, then alpha.
const PICKER_ORDER = [
  "en", "de", "fr", "it", "es", "pt", "nl", "pl",
  "sv", "da", "no", "fi", "ru", "cs", "hu", "el",
  "tr", "ar", "he", "ja", "ko", "zh", "hi", "th", "vi",
];

/**
 * TranslateCard — only renders when the recipe was scanned from a non-English
 * card. Offers "Translate to English" on first visit, then flips to
 * "Restore original ({language})" after a successful translate so the owner
 * can swap back one-click. Permission is gated on the parent's `canEdit`.
 */
export default function TranslateCard({ recipe, canEdit, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const lang = (recipe?.source_language || "").toLowerCase();
  const needsTranslate = lang && lang !== "en";
  if (!needsTranslate || !canEdit) return null;

  const langName = LANGUAGE_NAMES[lang] || lang.toUpperCase();

  const overrideLanguage = async (newLang) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/recipes/${recipe.recipe_id}/source-language`, { source_language: newLang });
      if (data?.recipe) onChanged?.(data.recipe);
      setPickerOpen(false);
      toast.success(newLang === "en" ? "Marked as English — no translation needed." : `Language set to ${LANGUAGE_NAMES[newLang] || newLang}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update language");
    } finally { setBusy(false); }
  };

  const translate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/recipes/${recipe.recipe_id}/translate-to-english`);
      if (data?.recipe) onChanged?.(data.recipe);
      toast.success("Translated to English — the original is kept so you can restore any time.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Translation failed");
    } finally { setBusy(false); }
  };

  const restore = async () => {
    if (!window.confirm(`Restore the original ${langName} wording? Your translation will be replaced.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/recipes/${recipe.recipe_id}/restore-original`);
      if (data?.recipe) onChanged?.(data.recipe);
      toast.success(`Original ${langName} restored.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed");
    } finally { setBusy(false); }
  };

  if (recipe.translated_to_en) {
    return (
      <div
        className="rounded-2xl bg-emerald-50 border border-emerald-200 px-5 py-3 my-2 flex items-center justify-between gap-3"
        data-testid="recipe-translate-card"
      >
        <div className="text-sm text-emerald-900">
          Translated from <strong>{langName}</strong> — the original wording is archived.
        </div>
        <button
          type="button"
          onClick={restore}
          disabled={busy}
          data-testid="recipe-restore-original"
          className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-900 hover:text-rf-ink disabled:opacity-50"
        >
          <ArrowCounterClockwise size={14} weight="bold" />
          {busy ? "Restoring…" : `Restore original ${langName}`}
        </button>
      </div>
    );
  }

  return (
    <div
      className="rounded-2xl bg-rf-amber/15 border border-rf-amber/40 px-5 py-3 my-2"
      data-testid="recipe-translate-card"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm text-rf-ink">
          This recipe is written in <strong>{langName}</strong>. Would you like an English version?
        </div>
        <button
          type="button"
          onClick={translate}
          disabled={busy}
          data-testid="recipe-translate-to-english"
          className="btn-amber !py-2 !px-4 text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
        >
          <Translate size={14} weight="bold" />
          {busy ? "Translating…" : "Translate to English"}
        </button>
      </div>
      {/* "Looks wrong?" language override — handy for mixed EN/DE cards
          where auto-detect lands on the wrong side. Click opens a small
          dropdown; picking "English" dismisses the banner entirely. */}
      <div className="mt-1.5 flex items-center justify-end gap-2 relative">
        <button
          type="button"
          onClick={() => setPickerOpen((v) => !v)}
          data-testid="recipe-translate-looks-wrong"
          className="text-[11px] text-rf-ink-soft hover:text-rf-ink underline underline-offset-2 inline-flex items-center gap-0.5"
          aria-expanded={pickerOpen}
        >
          Looks wrong? <CaretDown size={10} weight="bold" />
        </button>
        {pickerOpen && (
          <div
            className="absolute right-0 top-6 z-20 bg-white border border-rf-ink/15 rounded-xl shadow-xl p-1 max-h-72 overflow-y-auto w-56"
            data-testid="recipe-translate-language-picker"
          >
            <div className="text-[10px] uppercase tracking-wider font-black text-rf-ink-soft px-2 pt-1.5 pb-0.5">
              Original card is in…
            </div>
            {PICKER_ORDER.map((code) => (
              <button
                key={code}
                type="button"
                onClick={() => overrideLanguage(code)}
                disabled={busy || code === lang}
                data-testid={`recipe-translate-pick-${code}`}
                className={`w-full text-left text-xs px-2.5 py-1.5 rounded-md hover:bg-rf-amber/20 disabled:opacity-50 disabled:cursor-default flex items-center justify-between ${code === lang ? "font-bold text-rf-orange" : "text-rf-ink"}`}
              >
                <span>{LANGUAGE_NAMES[code] || code}</span>
                <span className="text-[10px] text-rf-ink-soft uppercase">{code}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
